import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  IconButton,
  Slider,
  Typography,
  MenuItem,
  Select,
  debounce,
} from "@mui/material";
import {
  PauseCircle,
  History,
  Update,
  Fullscreen,
  VolumeUp,
  VolumeOff,
  SkipNext,
} from "@mui/icons-material";
import PlayBtn from "../../../images/playButton.png";

const GhoramiPlayer = ({
  videoUrl,
  adUrl,
  subtitles = [],
  playerHeight = 400,
}) => {
  const videoRef = useRef(null);
  const adRef = useRef(null);
  const containerRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [muted, setMuted] = useState(false);
  const [fullscreenMode, setFullscreenMode] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [floating, setFloating] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [showAd, setShowAd] = useState(!!adUrl);
  const [skipAdVisible, setSkipAdVisible] = useState(false);
  const controlsTimeout = useRef(null);

  // Handle subtitles
  useEffect(() => {
    if (videoRef.current) {
      const tracks = videoRef.current.textTracks;
      Array.from(tracks).forEach((track) => (track.mode = "hidden"));
      const defaultTrack = Array.from(tracks).find((t) => t.default);
      if (defaultTrack) defaultTrack.mode = "showing";
    }
  }, [videoUrl]);

  // Auto-hide controls
  const resetControlTimeout = () => {
    setShowControls(true);
    clearTimeout(controlsTimeout.current);
    controlsTimeout.current = setTimeout(() => setShowControls(false), 3000);
  };

  useEffect(() => {
    if (playing) resetControlTimeout();
    return () => clearTimeout(controlsTimeout.current);
  }, [playing]);

  // Progress tracking
  useEffect(() => {
    const updateTime = () => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
        requestAnimationFrame(updateTime);
      }
    };
    if (playing) requestAnimationFrame(updateTime);
  }, [playing]);

  useEffect(() => {
    const handleMetadata = () => setDuration(videoRef.current?.duration || 0);
    videoRef.current?.addEventListener("loadedmetadata", handleMetadata);
    return () =>
      videoRef.current?.removeEventListener("loadedmetadata", handleMetadata);
  }, []);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.pause();
    else videoRef.current.play().catch(console.error);
    videoRef.current.volume = volume;
    videoRef.current.muted = muted;
    setPlaying(!playing);
  };

  const fastForward = () =>
    videoRef.current && (videoRef.current.currentTime += 5);
  const rewind = () => videoRef.current && (videoRef.current.currentTime -= 5);
  const toggleFullscreen = () => {
    const el = videoRef.current;
    if (!fullscreenMode) el?.requestFullscreen?.();
    else document.exitFullscreen?.();
    setFullscreenMode(!fullscreenMode);
  };

  const toggleMute = () => {
    const newMuted = !muted;
    setMuted(newMuted);
    if (videoRef.current) videoRef.current.muted = newMuted;
  };

  const setVol = (_, val) => {
    setVolume(val);
    setMuted(val <= 0.05);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val <= 0.05;
    }
  };

  const seekTo = (_, time) => {
    if (videoRef.current) videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const formatTime = (t) =>
    `${Math.floor(t / 60)}:${("0" + Math.floor(t % 60)).slice(-2)}`;

  const changeSpeed = (e) => {
    const newSpeed = parseFloat(e.target.value);
    setSpeed(newSpeed);
    if (videoRef.current) videoRef.current.playbackRate = newSpeed;
  };

  // Keyboard
  useEffect(() => {
    const keyHandler = (e) => {
      switch (e.key.toLowerCase()) {
        case " ":
          e.preventDefault();
          togglePlay();
          break;
        case "arrowright":
          fastForward();
          break;
        case "arrowleft":
          rewind();
          break;
        case "f":
          toggleFullscreen();
          break;
        case "m":
          toggleMute();
          break;
        case "arrowup":
          setVol(null, Math.min(1, volume + 0.05));
          break;
        case "arrowdown":
          setVol(null, Math.max(0, volume - 0.05));
          break;
      }
    };
    window.addEventListener("keydown", keyHandler);
    return () => window.removeEventListener("keydown", keyHandler);
  }, [volume, muted, playing]);

  // Floating on scroll
  // useEffect(() => {
  //   const observer = new IntersectionObserver(
  //     ([entry]) => setFloating(!entry.isIntersecting),
  //     { threshold: 0.5 }
  //   );
  //   if (containerRef.current) observer.observe(containerRef.current);
  //   return () => observer.disconnect();
  // }, []);
  const handleMouseMove = useCallback(
    debounce(() => {
      resetControlTimeout();
    }, 100),
    []
  );

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setFloating(true);
      } else {
        setFloating(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Skip Ad
  useEffect(() => {
    if (adUrl) {
      const ad = adRef.current;
      ad.volume = 0.5;
      ad.play();
      const skipTimeout = setTimeout(() => setSkipAdVisible(true), 3000);
      ad.onended = () => setShowAd(false);
      return () => clearTimeout(skipTimeout);
    }
  }, [adUrl]);

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className={`ghorami-player ${floating ? "floating" : ""}`}
      style={{
        position: floating ? "fixed" : "relative",
        transform: floating ? "translate(20px, -20px)" : "none",
        bottom: floating ? 20 : "auto",
        left: floating ? 20 : "auto",
        width: floating ? 300 : "100%",
        height: floating ? 180 : playerHeight,
        zIndex: 100000,
        background: "#000",
        borderRadius: "8px",
        overflow: "hidden",
        transition: "all 0.3s ease",
      }}
    >
      {showAd && (
        <video
          ref={adRef}
          src={adUrl}
          width="100%"
          height="100%"
          autoPlay
          style={{
            objectFit: "contain",
            cursor: "pointer",
            position: "absolute",
            zIndex: 10,
          }}
        />
      )}

      {showAd && skipAdVisible && (
        <IconButton
          onClick={() => {
            adRef.current.pause();
            setShowAd(false);
          }}
          style={{
            position: "absolute",
            zIndex: 11,
            top: 10,
            right: 10,
            background: "rgba(0,0,0,0.6)",
            color: "#fff",
          }}
        >
          <SkipNext />
          <Typography sx={{ ml: 1 }}>Skip Ad</Typography>
        </IconButton>
      )}

      <video
        id="video1"
        ref={videoRef}
        className="video"
        autoPlay={false}
        width="100%"
        disablePictureInPicture
        controlsList="nodownload"
        src={videoUrl}
        style={{
          width: "100%",
          height: "100%",
          cursor: "pointer",
          objectFit: "contain", // 🔑 This ensures full video is shown
        }}
      >
        {subtitles.map((track, i) => (
          <track
            key={i}
            label={track.label}
            kind="subtitles"
            srcLang={track.srclang}
            src={track.src}
            default={track.default}
          />
        ))}
      </video>

      {!playing && !showAd && (
        <Box
          onClick={togglePlay}
          sx={{
            position: "absolute",
            top: "45%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9,
            cursor: "pointer",
          }}
        >
          <Box component="img" src={PlayBtn} alt="Play" sx={{ width: 60 }} />
        </Box>
      )}

      {showControls && !showAd && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            background: "rgba(0,0,0,0.6)",
            p: 1,
            display: "flex",
            alignItems: "center",
            zIndex: 8,
          }}
        >
          <IconButton onClick={rewind} size="small" sx={{ color: "white" }}>
            <History fontSize="small" />
          </IconButton>
          <IconButton
            onClick={togglePlay}
            size="small"
            sx={{ color: playing ? "red" : "green" }}
          >
            {playing ? (
              <PauseCircle fontSize="medium" />
            ) : (
              <Box
                component="img"
                src={PlayBtn}
                alt="Play"
                sx={{ width: 25 }}
              />
            )}
          </IconButton>
          <IconButton
            onClick={fastForward}
            size="small"
            sx={{ color: "white" }}
          >
            <Update fontSize="small" />
          </IconButton>

          <Slider
            value={currentTime}
            onChange={seekTo}
            min={0}
            max={duration}
            step={0.1}
            sx={{ mx: 2, color: "white", flex: 1 }}
          />

          <Typography variant="caption" sx={{ color: "white", minWidth: 60 }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </Typography>

          <IconButton onClick={toggleMute} size="small" sx={{ color: "white" }}>
            {muted ? (
              <VolumeOff fontSize="small" />
            ) : (
              <VolumeUp fontSize="small" />
            )}
          </IconButton>

          <Slider
            value={volume}
            onChange={setVol}
            min={0}
            max={1}
            step={0.01}
            sx={{ width: 60, mx: 1, color: "white" }}
          />

          <Select
            value={speed}
            onChange={changeSpeed}
            size="small"
            sx={{
              color: "white",
              bgcolor: "transparent",
              mx: 1,
              borderColor: "white",
            }}
          >
            {[0.5, 1, 1.25, 1.5, 2].map((s) => (
              <MenuItem key={s} value={s}>
                {s}x
              </MenuItem>
            ))}
          </Select>

          <IconButton
            onClick={toggleFullscreen}
            size="small"
            sx={{ color: "white" }}
          >
            <Fullscreen fontSize="small" />
          </IconButton>
        </Box>
      )}
    </div>
  );
};

export default GhoramiPlayer;
